import {parseString} from '@github-ui/blackbird-query-parsing/parsing'
import {isContentNode, isQualifier, hasChildren} from '@github-ui/blackbird-query-parsing/common'
import type {SearchNode} from '../types/search-types'

/**
 * Checks whether the query AST contains an explicit issue type qualifier
 * (e.g. `is:issue` or `type:issue`) that is not negated.
 *
 * This function parses the query string into an AST and walks it to detect
 * explicit issue intent, handling edge cases like:
 * - Both `is:issue` and `type:issue` qualifiers
 * - Negated queries (e.g., `NOT is:issue`)
 * - Qualifiers inside quoted text (ignored)
 * - Case-sensitive matching for the qualifier value (`issue` only)
 *
 * @param query - The search query string
 * @returns true if the query contains an explicit, non-negated issue qualifier
 */
export function isExplicitIssueQuery(query: string): boolean {
  if (!query) return false

  const ast = parseString(query)
  return checkNode(ast, new Set(['issue']))
}

export function isPullRequestsOnlyQuery(query: string): boolean {
  if (!query) return false

  const ast = parseString(query)
  return checkNode(ast, new Set(['pr', 'pull-request'])) && !checkNode(ast, new Set(['issue', 'issues']))
}

function checkNode(node: SearchNode, values: ReadonlySet<string>, negated = false): boolean {
  if (node.kind === 'Not') {
    if (hasChildren(node)) {
      return node.children.some(child => checkNode(child, values, true))
    }
    return false
  }

  if (isQualifier(node) && isContentNode(node.content) && !negated) {
    const value = node.content.value.toString()
    if ((node.qualifier === 'Is' || node.qualifier === 'Type') && values.has(value)) return true
  }

  if (hasChildren(node)) {
    return node.children.some(child => checkNode(child, values, negated))
  }
  return false
}
